// Nearby Curve customers for one demo — the customer map's radius search, done before the call.
//
// Built by morning_briefs/nearby.py. The radius starts at 5 miles and widens in 5-mile
// steps to 30, stopping at the first radius with 10+ customers, so a dense metro shows a
// tight circle and a rural practice shows how thin the reference base is before the rep
// finds out live. When 30 miles never reaches 10, the brief reports the tightest radius
// that holds everyone found (2 at 5 miles and still 2 at 30 reads "2 within 5 miles").
// Distances are ZIP centre to ZIP centre, as on the customer map.
//
// The map is a real image, not a layout of tiles: OpenStreetMap tiles (the same tiles the
// customer map uses; they send Access-Control-Allow-Origin: *) are drawn into a canvas with
// the circle, the customer dots, a caption, a legend and the OSM credit, then shown as a
// PNG. The point is that a rep can copy it straight into an email to the prospect —
// "Copy map image", or right-click > Copy image — instead of opening the map, taking a
// screenshot and pasting that. The caption and legend are written for the prospect.
// If the canvas can't be built, the card falls back to the live tile layout (no copy).

import { useEffect, useState } from 'react'
import type { BriefNearby } from '../lib/types'

const TILE = 256
const CARD_W = 260          // on-screen width of the map on the card
const CARD_H = 180
const IMG_W = 600           // the image that gets copied: a sensible size inline in an email
const IMG_H = 400

// Salesforce's "None - New Office" means a startup with no previous PMS.
function pmsLabel(pms: string) {
  return /^none/i.test(pms) ? 'new office' : pms
}

function distanceLabel(mi: number, basis: BriefNearby['basis']) {
  if (mi === 0 && basis === 'zip') return 'same ZIP'
  return mi < 1 ? 'under 1 mi' : `${mi} mi`
}

// Web Mercator, in world pixels at zoom z.
function project(lat: number, lng: number, z: number) {
  const scale = TILE * 2 ** z
  const s = Math.sin((lat * Math.PI) / 180)
  return {
    x: ((lng + 180) / 360) * scale,
    y: (0.5 - Math.log((1 + s) / (1 - s)) / (4 * Math.PI)) * scale,
  }
}

// Zoom, offsets and the tiles covering a w x h view centred on the practice, with the
// whole circle (plus a margin) in view.
function viewport(n: BriefNearby, w: number, h: number) {
  const cos = Math.cos((n.lat * Math.PI) / 180)
  const mppNeeded = (2 * n.radius * 1609.344 * 1.25) / Math.min(w, h)
  const z = Math.max(3, Math.min(14, Math.floor(Math.log2((156543.03392 * cos) / mppNeeded))))
  const mpp = (156543.03392 * cos) / 2 ** z
  const c = project(n.lat, n.lng, z)
  const left = c.x - w / 2
  const top = c.y - h / 2
  const max = 2 ** z
  const tiles: { x: number; y: number; url: string }[] = []
  for (let tx = Math.floor(left / TILE); tx <= Math.floor((left + w) / TILE); tx++)
    for (let ty = Math.floor(top / TILE); ty <= Math.floor((top + h) / TILE); ty++)
      if (ty >= 0 && ty < max)
        tiles.push({ x: tx, y: ty, url: `https://tile.openstreetmap.org/${z}/${((tx % max) + max) % max}/${ty}.png` })
  return { z, left, top, tiles, r: (n.radius * 1609.344) / mpp }
}

function caption(n: BriefNearby) {
  return `${n.count} Curve Dental customer${n.count === 1 ? '' : 's'} within ${n.radius} mile${n.radius === 1 ? '' : 's'}`
}

function roundRect(g: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  g.beginPath()
  g.moveTo(x + r, y)
  g.arcTo(x + w, y, x + w, y + h, r)
  g.arcTo(x + w, y + h, x, y + h, r)
  g.arcTo(x, y + h, x, y, r)
  g.arcTo(x, y, x + w, y, r)
  g.closePath()
}

function dot(g: CanvasRenderingContext2D, x: number, y: number, r: number, fill: string, ring: number) {
  g.beginPath()
  g.arc(x, y, r, 0, 2 * Math.PI)
  g.fillStyle = fill
  g.fill()
  g.lineWidth = ring
  g.strokeStyle = '#ffffff'
  g.stroke()
}

// Draw the email-ready PNG. Rejects if the canvas can't be exported (e.g. a tile came
// back without CORS, which taints the canvas).
async function renderMapPng(n: BriefNearby): Promise<Blob> {
  const v = viewport(n, IMG_W, IMG_H)
  const cv = document.createElement('canvas')
  cv.width = IMG_W
  cv.height = IMG_H
  const g = cv.getContext('2d')
  if (!g) throw new Error('no canvas')
  g.fillStyle = '#e8e6e1'
  g.fillRect(0, 0, IMG_W, IMG_H)

  await Promise.all(v.tiles.map((t) => new Promise<void>((resolve) => {
    const im = new Image()
    im.crossOrigin = 'anonymous'
    im.onload = () => { g.drawImage(im, t.x * TILE - v.left, t.y * TILE - v.top, TILE, TILE); resolve() }
    im.onerror = () => resolve()       // a missing tile leaves a grey square, not a broken image
    im.src = t.url
  })))

  // The radius.
  g.beginPath()
  g.arc(IMG_W / 2, IMG_H / 2, v.r, 0, 2 * Math.PI)
  g.fillStyle = 'rgba(47, 95, 196, 0.10)'
  g.fill()
  g.setLineDash([9, 7])
  g.lineWidth = 2.5
  g.strokeStyle = '#2f5fc4'
  g.stroke()
  g.setLineDash([])

  // The practice first and larger, customers on top: a customer in the same town (often
  // at the same point) then shows as a green dot inside a red ring instead of vanishing.
  dot(g, IMG_W / 2, IMG_H / 2, 10, '#dc2626', 3)
  for (const p of n.points) {
    const q = project(p.lat, p.lng, v.z)
    dot(g, q.x - v.left, q.y - v.top, 6.5, '#16a34a', 2)
  }

  const font = 'system-ui, "Segoe UI", Roboto, Helvetica, Arial, sans-serif'

  // Caption, top left.
  const title = caption(n)
  const sub = n.place ? `of ${n.place}` : ''
  g.font = `600 19px ${font}`
  const tw = g.measureText(title).width
  g.font = `400 14px ${font}`
  const sw = sub ? g.measureText(sub).width : 0
  const boxW = Math.max(tw, sw) + 28
  const boxH = sub ? 58 : 38
  roundRect(g, 12, 12, boxW, boxH, 8)
  g.fillStyle = 'rgba(255,255,255,0.94)'
  g.fill()
  g.fillStyle = '#111827'
  g.font = `600 19px ${font}`
  g.fillText(title, 26, 38)
  if (sub) {
    g.fillStyle = '#4b5563'
    g.font = `400 14px ${font}`
    g.fillText(sub, 26, 58)
  }

  // Legend, bottom left.
  g.font = `400 13px ${font}`
  const l1 = 'Curve Dental customer'
  const l2 = 'Your practice'
  const legW = 34 + g.measureText(l1).width + 30 + g.measureText(l2).width + 14
  roundRect(g, 12, IMG_H - 42, legW, 30, 7)
  g.fillStyle = 'rgba(255,255,255,0.94)'
  g.fill()
  dot(g, 28, IMG_H - 27, 6, '#16a34a', 1.5)
  g.fillStyle = '#111827'
  g.fillText(l1, 40, IMG_H - 22)
  const x2 = 40 + g.measureText(l1).width + 22
  dot(g, x2, IMG_H - 27, 6, '#dc2626', 1.5)
  g.fillStyle = '#111827'
  g.fillText(l2, x2 + 12, IMG_H - 22)

  // OpenStreetMap's licence requires the credit on the image.
  g.font = `400 11px ${font}`
  const credit = '© OpenStreetMap contributors'
  const cw = g.measureText(credit).width + 10
  g.fillStyle = 'rgba(255,255,255,0.85)'
  g.fillRect(IMG_W - cw, IMG_H - 17, cw, 17)
  g.fillStyle = '#374151'
  g.fillText(credit, IMG_W - cw + 5, IMG_H - 5)

  return new Promise<Blob>((resolve, reject) => {
    try {
      cv.toBlob((b) => (b ? resolve(b) : reject(new Error('export failed'))), 'image/png')
    } catch (e) {
      reject(e)
    }
  })
}

// The live tile layout: used only if the image can't be built.
function TileMap({ n }: { n: BriefNearby }) {
  const v = viewport(n, CARD_W, CARD_H)
  return (
    <div
      className="relative shrink-0 overflow-hidden rounded border border-marker-border bg-marker-panel2"
      style={{ width: CARD_W, height: CARD_H }}
      role="img"
      aria-label={`Map: ${caption(n)}${n.place ? ` of ${n.place}` : ''}`}
    >
      {v.tiles.map((t) => (
        <img key={`${t.x}-${t.y}`} src={t.url} alt="" loading="lazy" draggable={false}
             className="absolute max-w-none select-none"
             style={{ left: t.x * TILE - v.left, top: t.y * TILE - v.top, width: TILE, height: TILE }} />
      ))}
      <svg className="absolute inset-0" width={CARD_W} height={CARD_H}>
        <circle cx={CARD_W / 2} cy={CARD_H / 2} r={v.r} fill="rgba(91,141,239,0.10)" stroke="#4a76d1"
                strokeWidth={1.5} strokeDasharray="5 4" />
        <circle cx={CARD_W / 2} cy={CARD_H / 2} r={7} fill="#dc2626" stroke="#fff" strokeWidth={2} />
        {n.points.map((p, i) => {
          const q = project(p.lat, p.lng, v.z)
          return (
            <circle key={i} cx={q.x - v.left} cy={q.y - v.top} r={4} fill="#16a34a" stroke="#fff" strokeWidth={1.5}>
              <title>{`${p.name} (${distanceLabel(p.miles, n.basis)})`}</title>
            </circle>
          )
        })}
      </svg>
      <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noreferrer"
         className="absolute bottom-0 right-0 bg-white/80 px-1 text-[9px] leading-tight text-neutral-700">
        © OpenStreetMap
      </a>
    </div>
  )
}

function NearbyMap({ n, account }: { n: BriefNearby; account: string }) {
  const [png, setPng] = useState<{ blob: Blob; url: string } | null>(null)
  const [failed, setFailed] = useState(false)
  const [copy, setCopy] = useState<'idle' | 'copied' | 'error'>('idle')

  useEffect(() => {
    let url = ''
    let live = true
    renderMapPng(n)
      .then((blob) => {
        if (!live) return
        url = URL.createObjectURL(blob)
        setPng({ blob, url })
      })
      .catch(() => live && setFailed(true))
    return () => {
      live = false
      if (url) URL.revokeObjectURL(url)
    }
  }, [n])

  if (failed) return <TileMap n={n} />

  async function copyImage() {
    if (!png) return
    try {
      await navigator.clipboard.write([new ClipboardItem({ 'image/png': png.blob })])
      setCopy('copied')
      window.setTimeout(() => setCopy('idle'), 2500)
    } catch {
      setCopy('error')
    }
  }

  const file = `Curve customers near ${account}`.replace(/[^\w\- ]+/g, '').trim().replace(/\s+/g, '_') + '.png'

  return (
    <div className="shrink-0" style={{ width: CARD_W }}>
      {png ? (
        <img
          src={png.url}
          alt={`${caption(n)}${n.place ? ` of ${n.place}` : ''}`}
          width={CARD_W}
          height={Math.round((CARD_W * IMG_H) / IMG_W)}
          className="block rounded border border-marker-border"
        />
      ) : (
        <div
          className="rounded border border-marker-border bg-marker-panel2 animate-pulse"
          style={{ width: CARD_W, height: Math.round((CARD_W * IMG_H) / IMG_W) }}
        />
      )}
      <div className="mt-1.5 flex items-center gap-3 text-xs">
        <button
          onClick={copyImage}
          disabled={!png}
          title="Copies the map as an image, ready to paste into an email"
          className="px-2 py-1 rounded border border-marker-accent/40 text-marker-accent
                     hover:bg-marker-accent/10 disabled:opacity-40 transition-colors
                     focus:outline-none focus:ring-2 focus:ring-marker-accent/50"
        >
          {copy === 'copied' ? 'Copied' : 'Copy map image'}
        </button>
        {png && (
          <a href={png.url} download={file} className="text-marker-muted hover:text-marker-text underline-offset-2 hover:underline">
            Download
          </a>
        )}
      </div>
      <div className="mt-1 text-[11px] text-marker-muted leading-snug">
        {copy === 'error'
          ? 'Your browser blocked the copy. Right-click the map and choose Copy image.'
          : copy === 'copied'
            ? 'Paste it straight into your email.'
            : 'For your email to the practice.'}
      </div>
    </div>
  )
}

export function NearbyCustomers({ n, account }: { n: BriefNearby | null | undefined; account: string }) {
  if (!n) return null
  if (n.status !== 'ok') {
    return (
      <div className="mt-3 text-xs text-marker-muted">
        <span className="text-marker-text/70">Nearby customers:</span> no ZIP or town on the
        Salesforce account, so nothing to search from.
      </div>
    )
  }
  const thin = !n.enough
  const searched = n.searched ?? 30
  const headline =
    n.count === 0
      ? `None within ${n.radius} miles`
      : `${n.count} within ${n.radius} mile${n.radius === 1 ? '' : 's'}`

  return (
    <div className="mt-3 rounded-md border border-marker-border px-3 py-2.5">
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-wider text-marker-muted mb-1">
            Nearby Curve customers
          </div>
          <div className={'text-sm font-semibold ' + (thin ? 'text-marker-warn' : 'text-marker-text')}>
            {headline}
            {n.place && <span className="font-normal text-marker-muted"> of {n.place}</span>}
          </div>
          {thin && n.count > 0 && (
            <div className="text-xs text-marker-warn/90 mt-0.5">
              {n.radius < searched
                ? `No others within ${searched} miles. Worth knowing before they ask.`
                : `Few customers nearby, even at ${n.radius} miles. Worth knowing before they ask.`}
            </div>
          )}
          {n.count === 0 && n.nearest && (
            <div className="text-xs text-marker-warn/90 mt-0.5">
              Nearest is {n.nearest.name} ({n.nearest.city}{n.nearest.state ? `, ${n.nearest.state}` : ''},{' '}
              {distanceLabel(n.nearest.miles, n.basis)}).
            </div>
          )}
          {n.closest.length > 0 && (
            <ul className="mt-2 space-y-1">
              {n.closest.map((c, i) => (
                <li key={i} className="text-sm text-marker-text/90 leading-snug">
                  {c.name}
                  <span className="text-marker-muted">
                    {' '}· {c.city} · {distanceLabel(c.miles, n.basis)}{c.pms ? ` · ${pmsLabel(c.pms)}` : ''}
                  </span>
                </li>
              ))}
            </ul>
          )}
          <div className="mt-2 text-[11px] text-marker-muted">
            {n.basis === 'town'
              ? 'Measured from the town centre: the account has no ZIP. '
              : `Measured from ZIP ${n.zip}. `}
            Same rules as the customer map.
          </div>
        </div>
        {n.count > 0 && <NearbyMap n={n} account={account} />}
      </div>
    </div>
  )
}
